/**
 * Dashboard de Talent Acquisition — las métricas fijas del documento técnico
 * "Agente TA Metrics" (sección 7), calculadas en el backend con la MISMA
 * normalización que usa el bot. Nada se recalcula acá: solo se muestra.
 *
 * Bloques: cartera abierta + ageing · estados previos (nunca se suman a
 * Abiertas) · cierres YTD vs mismo período del año anterior (externas vs
 * internas) · cierres por mes · tiempo de cierre por trimestre · cortes de la
 * cartera · calidad de datos conocida.
 */

import { currentLocale } from '@/lib/locale';
import { StatCard } from '@/components/ui/stat-card';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell,
} from 'recharts';
import { Briefcase, Clock, TrendingUp, AlertTriangle, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TaDashboard as TaDashboardData, TaTiempoPorTipo } from '@/../../shared/types/ta-metrics';
import type { CorteFila } from '@/../../shared/ta-metrics';

const fmt = (n: number | null | undefined, d = 1) => (n === null || n === undefined ? '—' : n.toLocaleString(currentLocale(), { maximumFractionDigits: d }));
const pct = (a: number, b: number) => (b === 0 ? null : Math.round(((a - b) / b) * 100));
const PALETTE = ['#1CC9B9', '#6366f1', '#f59e0b', '#ec4899', '#22c55e', '#3b82f6', '#8b5cf6', '#f97316', '#14b8a6', '#84cc16'];

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' });
}


function CorteBar({ title, rows, total, layout = 'vertical' }: { title: string; rows: CorteFila[]; total: number; layout?: 'vertical' | 'horizontal' }) {
  const { t } = useTranslation();
  const data = rows.slice(0, 10).map((r) => ({ name: r.valor, n: r.n }));
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {title} <span className="ml-1 text-sm font-normal text-muted-foreground">({t('talent_acquisition.dashboard.total_n', { count: total })})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('talent_acquisition.dashboard.empty')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(160, data.length * 28 + 40)}>
            <BarChart data={data} layout={layout === 'vertical' ? 'vertical' : 'horizontal'} margin={{ left: layout === 'vertical' ? 8 : 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={layout !== 'vertical'} vertical={layout === 'vertical'} />
              {layout === 'vertical' ? (
                <>
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12 }} />
                </>
              ) : (
                <>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                </>
              )}
              <Tooltip formatter={(v) => [v, t('talent_acquisition.dashboard.searches')]} />
              <Bar dataKey="n" radius={layout === 'vertical' ? [0, 4, 4, 0] : [4, 4, 0, 0]}>
                {data.map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function TiempoRow({ t: tt, row, refRow }: { t: (k: string, o?: Record<string, unknown>) => string; row: TaTiempoPorTipo; refRow?: TaTiempoPorTipo }) {
  const delta = refRow ? pct(row.cierres, refRow.cierres) : null;
  return (
    <TableRow>
      <TableCell className="font-medium whitespace-nowrap">{row.label}</TableCell>
      <TableCell className="text-right tabular-nums">
        {row.cierres}
        {delta !== null && <span className={cn('ml-1 text-xs', delta >= 0 ? 'text-emerald-600' : 'text-red-600')}>({delta >= 0 ? '+' : ''}{delta}%)</span>}
      </TableCell>
      <TableCell className="text-right tabular-nums">{fmt(row.mediana_dias)}</TableCell>
      <TableCell className="text-right tabular-nums">{fmt(row.promedio_dias)}</TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">{row.N}</TableCell>
      <TableCell className="text-right tabular-nums">{fmt(row.externas.mediana_dias)} <span className="text-xs text-muted-foreground">(N {row.externas.N})</span></TableCell>
      <TableCell className="text-right tabular-nums">{fmt(row.internas.mediana_dias)} <span className="text-xs text-muted-foreground">(N {row.internas.N})</span></TableCell>
      <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
        {row.excluidas_sin_fechas > 0 && tt('talent_acquisition.dashboard.excluded', { count: row.excluidas_sin_fechas })}
        {row.negativos > 0 && ` · ${tt('talent_acquisition.dashboard.negatives', { count: row.negativos })}`}
      </TableCell>
    </TableRow>
  );
}

export function TaDashboard({ data }: { data: TaDashboardData }) {
  const { t } = useTranslation();
  const k = 'talent_acquisition.dashboard';
  const { cartera, estados, cierres, calidad } = data;
  const ytdDelta = pct(cierres.ytd.cierres, cierres.ytdAnterior.cierres);
  const mesData = cierres.porMes.map((m) => ({ ...m, name: monthLabel(m.label) }));
  const est = (e: string) => estados.conteo[e] ?? 0;

  const calidadItems: Array<{ key: keyof typeof calidad; n: number }> = [
    { key: 'sin_fecha_apertura', n: calidad.sin_fecha_apertura },
    { key: 'cierres_negativos', n: calidad.cierres_negativos },
    { key: 'cerradas_sin_hire_date', n: calidad.cerradas_sin_hire_date },
    { key: 'canceladas_con_hire_date', n: calidad.canceladas_con_hire_date },
    { key: 'sin_tipo_definido', n: calidad.sin_tipo_definido },
    { key: 'filas_con_id_duplicado', n: calidad.filas_con_id_duplicado },
    { key: 'estado_otro', n: calidad.estado_otro },
  ];

  return (
    <div className="space-y-6">
      {/* Cartera hoy */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard layout="left" iconBox={false} size="lg" iconClassName="text-brand-green" icon={Briefcase} label={t(`${k}.open_today`)} value={cartera.abiertas} sub={t(`${k}.open_hint`)} />
        <StatCard layout="left" iconBox={false} size="lg" icon={Clock} label={t(`${k}.ageing_median`)} value={fmt(cartera.ageing.mediana, 0)} sub={t(`${k}.ageing_hint`, { over30: cartera.ageing.mas_de_30, over60: cartera.ageing.mas_de_60, avg: fmt(cartera.ageing.promedio) })} iconClassName="text-amber-500" />
        <StatCard layout="left" iconBox={false} size="lg"
          icon={TrendingUp}
          label={t(`${k}.closed_ytd`, { year: data.hoy.slice(0, 4) })}
          value={cierres.ytd.cierres}
          sub={t(`${k}.closed_ytd_hint`, { prev: cierres.ytdAnterior.cierres, delta: ytdDelta === null ? '—' : `${ytdDelta >= 0 ? '+' : ''}${ytdDelta}%` })}
          iconClassName="text-emerald-500"
        />
        <StatCard layout="left" iconBox={false} size="lg"
          icon={Clock}
          label={t(`${k}.time_to_close_ytd`)}
          value={fmt(cierres.ytd.mediana_dias)}
          sub={t(`${k}.time_to_close_hint`, { avg: fmt(cierres.ytd.promedio_dias), prevMedian: fmt(cierres.ytdAnterior.mediana_dias), n: cierres.ytd.N })}
          iconClassName="text-indigo-500"
        />
      </div>

      {/* Estados previos: aparte de Abiertas (regla 5.2) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t(`${k}.other_states`)}</CardTitle>
          <p className="text-xs text-muted-foreground">{t(`${k}.other_states_hint`)}</p>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {['Request', 'Pipeline', 'Programada', 'Stand By', 'Cancelada', 'Cerrada'].map((e) => (
            <div key={e} className="rounded-lg border p-3">
              <p className="text-2xl font-bold tabular-nums">{est(e)}</p>
              <p className="text-xs text-muted-foreground">{t(`${k}.state.${e}`)}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Cierres por mes + tiempo de cierre */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t(`${k}.monthly_title`)}</CardTitle>
          <p className="text-xs text-muted-foreground">{t(`${k}.monthly_hint`)}</p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={mesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="cierres" name={t(`${k}.series_closed`)} fill="#1CC9B9" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="aperturas" name={t(`${k}.series_opened`)} fill="#c7d2fe" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="mediana" name={t(`${k}.series_median`)} stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Tiempo de cierre por período */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t(`${k}.periods_title`)}</CardTitle>
          <p className="text-xs text-muted-foreground">{t(`${k}.periods_hint`)}</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t(`${k}.col_period`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_closed`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_median`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_avg`)}</TableHead>
                <TableHead className="text-right">N</TableHead>
                <TableHead className="text-right">{t(`${k}.col_external_median`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_internal_median`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_notes`)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TiempoRow t={t} row={cierres.ytd} refRow={cierres.ytdAnterior} />
              <TiempoRow t={t} row={cierres.ytdAnterior} />
              {cierres.porTrimestre.map((q) => <TiempoRow key={q.label} t={t} row={q} />)}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Cortes de la cartera abierta */}
      <div className="grid gap-4 lg:grid-cols-2">
        <CorteBar title={t(`${k}.open_by_recruiter`)} rows={cartera.porRecruiter} total={cartera.abiertas} />
        <CorteBar title={t(`${k}.open_by_area`)} rows={cartera.porArea} total={cartera.abiertas} />
        <CorteBar title={t(`${k}.open_by_country`)} rows={cartera.porPais} total={cartera.abiertas} />
        <CorteBar title={t(`${k}.open_by_stage`)} rows={cartera.porEtapa} total={cartera.abiertas} />
      </div>

      {/* Cierres por mes: externas vs internas (Sol 18/09) — debajo de los cortes de la cartera, mismo estilo fino que el mensual */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t(`${k}.monthly_type_title`)}</CardTitle>
          <p className="text-xs text-muted-foreground">{t(`${k}.monthly_type_hint`)}</p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={mesData} margin={{ top: 16 }} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="externas" stackId="c" name={t(`${k}.series_externas`)} fill="#1CC9B9" maxBarSize={28} label={{ position: 'inside', fontSize: 10, fill: '#fff', formatter: (v) => (Number(v) > 0 ? String(v) : '') }} />
              <Bar dataKey="internas" stackId="c" name={t(`${k}.series_internas`)} fill="#6366f1" maxBarSize={28} label={{ position: 'inside', fontSize: 10, fill: '#fff', formatter: (v) => (Number(v) > 0 ? String(v) : '') }} />
              <Bar dataKey="sinDefinir" stackId="c" name={t(`${k}.series_sin_definir`)} fill="#cbd5e1" maxBarSize={28} radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10, formatter: (v) => (Number(v) > 0 ? String(v) : '') }} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>


      <div className="grid gap-4 lg:grid-cols-2">
        {/* Request / Pipeline por recruiter */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t(`${k}.rp_title`)}</CardTitle>
            <p className="text-xs text-muted-foreground">{t(`${k}.rp_hint`)}</p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t(`${k}.col_recruiter`)}</TableHead>
                  <TableHead className="text-right">Request</TableHead>
                  <TableHead className="text-right">Pipeline</TableHead>
                  <TableHead className="text-right">{t(`${k}.col_total`)}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {estados.requestPipelinePorRecruiter.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="py-6 text-center text-muted-foreground">{t(`${k}.empty`)}</TableCell></TableRow>
                )}
                {estados.requestPipelinePorRecruiter.map((r) => (
                  <TableRow key={r.recruiter}>
                    <TableCell className="font-medium">{r.recruiter}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.request}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.pipeline}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{r.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Más antiguas */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t(`${k}.oldest_title`)}</CardTitle>
            <p className="text-xs text-muted-foreground">{t(`${k}.oldest_hint`, { date: data.hoy })}</p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t(`${k}.col_id`)}</TableHead>
                  <TableHead>{t(`${k}.col_position`)}</TableHead>
                  <TableHead>{t(`${k}.col_recruiter`)}</TableHead>
                  <TableHead className="text-right">{t(`${k}.col_days`)}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cartera.masAntiguas.map((r) => (
                  <TableRow key={`${r.ID}-${r.posicion}`}>
                    <TableCell className="font-mono text-xs">{r.ID}</TableCell>
                    <TableCell>
                      <div className="font-medium">{r.posicion || '—'}</div>
                      <div className="text-xs text-muted-foreground">{r.area}</div>
                    </TableCell>
                    <TableCell>{r.recruiter}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.dias}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Cierres YTD por recruiter */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t(`${k}.closed_by_recruiter`, { year: data.hoy.slice(0, 4) })}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t(`${k}.col_recruiter`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_closed`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_median`)}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_avg`)}</TableHead>
                <TableHead className="text-right">N</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cierres.porRecruiterYtd.map((r) => (
                <TableRow key={r.recruiter}>
                  <TableCell className="font-medium">{r.recruiter}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.cierres}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.mediana)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.promedio)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{r.N}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Calidad de datos */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
            {t(`${k}.quality_title`)}
            <span className="text-sm font-normal text-muted-foreground">({t(`${k}.rows_n`, { count: calidad.filas })})</span>
          </CardTitle>
          <p className="text-xs text-muted-foreground">{t(`${k}.quality_hint`)}</p>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {calidadItems.map(({ key, n }) => (
            <div key={key} className={cn('rounded-lg border p-3', n > 0 && 'border-amber-200 dark:border-amber-900')}>
              <p className={cn('flex items-center gap-1 text-2xl font-bold tabular-nums', n > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}>
                {n > 0 && <AlertTriangle className="h-4 w-4" />}{n}
              </p>
              <p className="text-xs text-muted-foreground">{t(`${k}.quality.${key}`)}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
